import { DEFAULT_SUPPLEMENTAL_ITEMS_PATH } from '../constants'
import { getNested } from '../utils'

/**
 * Client-side mapping: pipeline row arrays -> per-status-report-line payloads using matchConfig.
 */

function looseEq (a, b) {
  if (a === b) return true
  if (a != null && b != null && String(a) === String(b)) return true
  return false
}

function resolveMatchValueForLine (value, line) {
  if (value == null) return value
  if (typeof value === 'string') {
    if (value === '$codeName') return line?.codeName
    if (value === '$statusReportItemId') return line?.statusReportItemId
    return value
  }
  if (Array.isArray(value)) {
    return value.map((v) => resolveMatchValueForLine(v, line))
  }
  if (typeof value === 'object') {
    const out = {}
    for (const [k, v] of Object.entries(value)) {
      out[k] = resolveMatchValueForLine(v, line)
    }
    return out
  }
  return value
}

export function pipelineRowMatchesLine (row, matchConfig, line) {
  if (!matchConfig?.field || !matchConfig?.op) return false
  const rowVal = getNested(row, matchConfig.field)
  const resolved = resolveMatchValueForLine(matchConfig.value, line)
  const op = matchConfig.op

  switch (op) {
    case 'eq':
      return looseEq(rowVal, resolved)
    case 'neq':
      return !looseEq(rowVal, resolved)
    case 'in':
      return Array.isArray(resolved) && resolved.some((v) => looseEq(rowVal, v))
    case 'nin':
      return Array.isArray(resolved) && !resolved.some((v) => looseEq(rowVal, v))
    default:
      return false
  }
}

export function filterPipelineRowsForLine (rows, matchConfig, line) {
  if (!Array.isArray(rows) || !rows.length) return []
  if (!matchConfig?.field || !matchConfig?.op) return []
  return rows.filter((row) => pipelineRowMatchesLine(row, matchConfig, line))
}

/**
 * Build data object expected by supplemental layouts (default itemsPath {@link DEFAULT_SUPPLEMENTAL_ITEMS_PATH}).
 */
export function wrapMatchedRowsForDisplay (rows, displayConfig) {
  const path = (displayConfig?.itemsPath ?? DEFAULT_SUPPLEMENTAL_ITEMS_PATH).split('.').filter(Boolean)
  if (!path.length) return { [DEFAULT_SUPPLEMENTAL_ITEMS_PATH]: rows }
  const root = {}
  let cur = root
  for (let i = 0; i < path.length - 1; i++) {
    cur[path[i]] = {}
    cur = cur[path[i]]
  }
  cur[path[path.length - 1]] = rows
  return root
}
